import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Rating } from "@/components/ui/rating";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface ChatRatingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  characterId?: string;
}

export interface ChatRatingFormData {
  fun: number;
  interesting: number;
  realistic: number;
  comment: string;
}

const defaultValues: ChatRatingFormData = {
  fun: 0,
  interesting: 0,
  realistic: 0,
  comment: "",
};

export const ChatRatingModal: React.FC<ChatRatingModalProps> = ({
  open,
  onOpenChange,
  characterId,
}) => {
  const form = useForm<ChatRatingFormData>({
    defaultValues,
    mode: "onChange",
  });

  const mutation = useMutation({
    mutationFn: async (data: ChatRatingFormData) => {
      if (!characterId) throw new Error("Missing character");
      const { error } = await supabase.functions.invoke(
        "submit-character-rating",
        {
          body: {
            characterId,
            ...data,
          },
        },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      onOpenChange(false);
      form.reset(defaultValues);
      toast.success("Merci pour votre retour !");
    },
    onError: (error) => {
      toast.error("Une erreur est survenue lors de l'envoi de l'avis.");
      console.error(error);
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset(defaultValues);
    }
  }, [form, open]);

  const handleSubmit = form.handleSubmit((data) => {
    mutation.mutate(data);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Donner mon avis</DialogTitle>
          <DialogDescription>
            Merci de donner votre avis sur cette conversation. Votre retour nous
            aide à améliorer l'application !
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="fun"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel className="mb-0 flex items-center">Fun</FormLabel>
                  <FormControl>
                    <Rating
                      value={field.value}
                      onValueChange={field.onChange}
                      name="fun"
                      precision={1}
                      size={28}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="interesting"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel className="mb-0 flex items-center">
                    Intéressant
                  </FormLabel>
                  <FormControl>
                    <Rating
                      value={field.value}
                      onValueChange={field.onChange}
                      name="interesting"
                      precision={1}
                      size={28}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="realistic"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel className="mb-0 flex items-center">
                    Réaliste
                  </FormLabel>
                  <FormControl>
                    <Rating
                      value={field.value}
                      onValueChange={field.onChange}
                      name="realistic"
                      precision={1}
                      size={28}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="">Commentaire</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Votre commentaire..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                Envoyer
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
